import { isValidDocumentExtension, isZipFile } from "@/domain/classification/uploadFiles";

import type { FolderScanResult } from "@/application/ports/services/IFolderScanner";

function fileEntryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const allEntries: FileSystemEntry[] = [];

    function readBatch(): void {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(allEntries);
        } else {
          allEntries.push(...batch);
          readBatch();
        }
      }, reject);
    }

    readBatch();
  });
}

async function collectFilesFromEntry(
  entry: FileSystemEntry,
): Promise<{ valid: File[]; zips: File[]; ignored: number }> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await fileEntryToFile(fileEntry);

    if (isZipFile(file)) return { valid: [], zips: [file], ignored: 0 };
    if (isValidDocumentExtension(entry.name)) return { valid: [file], zips: [], ignored: 0 };
    return { valid: [], zips: [], ignored: 1 };
  }

  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const entries = await readAllEntries(reader);

    const results = await Promise.all(entries.map(collectFilesFromEntry));
    return {
      valid: results.flatMap((r) => r.valid),
      zips: results.flatMap((r) => r.zips),
      ignored: results.reduce((sum, r) => sum + r.ignored, 0),
    };
  }

  return { valid: [], zips: [], ignored: 0 };
}

export function filterFolderFileList(fileList: FileList | File[]): FolderScanResult {
  const validFiles: File[] = [];
  const zipFiles: File[] = [];
  let ignoredCount = 0;

  for (const file of Array.from(fileList)) {
    if (isZipFile(file)) {
      zipFiles.push(file);
    } else if (isValidDocumentExtension(file.name)) {
      validFiles.push(file);
    } else {
      ignoredCount++;
    }
  }

  return {
    validFiles,
    zipFiles,
    ignoredCount,
    totalScanned: validFiles.length + zipFiles.length + ignoredCount,
  };
}

export async function scanEntries(entries: FileSystemEntry[]): Promise<FolderScanResult> {
  const results = await Promise.all(entries.map(collectFilesFromEntry));
  const validFiles = results.flatMap((r) => r.valid);
  const zipFiles = results.flatMap((r) => r.zips);
  const ignoredCount = results.reduce((sum, r) => sum + r.ignored, 0);

  return {
    validFiles,
    zipFiles,
    ignoredCount,
    totalScanned: validFiles.length + zipFiles.length + ignoredCount,
  };
}
